import _toConsumableArray from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/toConsumableArray.js";
import React from "react";
var __jsx = React.createElement;
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { UnstyledButton } from "@mantine/core";
import { useAuth } from "@qure/qure-auth";
import { faCalendar, faClipboardList, faFileLines } from "@fortawesome/pro-regular-svg-icons";
import { useSelectedPatientStore } from "@/stores/selected-patient";
import { usePatientReports } from "@/hooks/report";
import { usePatientAppointments } from "@/hooks/appointments";
import { usePatientEncountersInfinite } from "@/hooks/encounters";
import { formatTimestamp } from "@/utils/format";
import { trackEvent, ANALYTICS_EVENTS } from "@/utils/analytics";
import { ResourceTab } from "@/components/link-patient-records-modal";
import { RecordRow } from "./record-row";
/** Self-contained panel showing all linked patient records, with an "Add records" shortcut. */
export function LinkedRecordsPanel(_ref) {
  var _userInfo$default_wor;
  var linkedResources = _ref.linkedResources,
    onAddRecords = _ref.onAddRecords,
    onViewRecords = _ref.onViewRecords;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useAuth = useAuth(),
    userInfo = _useAuth.userInfo;
  var workspaceId = (_userInfo$default_wor = userInfo === null || userInfo === void 0 ? void 0 : userInfo.default_workspace) !== null && _userInfo$default_wor !== void 0 ? _userInfo$default_wor : null;
  var patientUid = useSelectedPatientStore(function (s) {
    var _s$selectedPatient;
    return ((_s$selectedPatient = s.selectedPatient) === null || _s$selectedPatient === void 0 ? void 0 : _s$selectedPatient.patient_uid) != null ? Number(s.selectedPatient.patient_uid) : null;
  });
  var enabled = Boolean(workspaceId && patientUid);
  var _usePatientReports = usePatientReports(patientUid !== null && patientUid !== void 0 ? patientUid : 0, enabled),
    _usePatientReports$da = _usePatientReports.data,
    reports = _usePatientReports$da === void 0 ? [] : _usePatientReports$da;
  var _usePatientAppointmen = usePatientAppointments(workspaceId !== null && workspaceId !== void 0 ? workspaceId : 0, patientUid !== null && patientUid !== void 0 ? patientUid : 0),
    appointmentsData = _usePatientAppointmen.data;
  var encountersQuery = usePatientEncountersInfinite(workspaceId !== null && workspaceId !== void 0 ? workspaceId : 0, patientUid !== null && patientUid !== void 0 ? patientUid : 0);
  var allAppointments = useMemo(function () {
    var _appointmentsData$tod, _appointmentsData$fut, _appointmentsData$pas;
    return [].concat(_toConsumableArray((_appointmentsData$tod = appointmentsData === null || appointmentsData === void 0 ? void 0 : appointmentsData.today) !== null && _appointmentsData$tod !== void 0 ? _appointmentsData$tod : []), _toConsumableArray((_appointmentsData$fut = appointmentsData === null || appointmentsData === void 0 ? void 0 : appointmentsData.future) !== null && _appointmentsData$fut !== void 0 ? _appointmentsData$fut : []), _toConsumableArray((_appointmentsData$pas = appointmentsData === null || appointmentsData === void 0 ? void 0 : appointmentsData.past) !== null && _appointmentsData$pas !== void 0 ? _appointmentsData$pas : []));
  }, [appointmentsData]);
  var allEncounters = useMemo(function () {
    var _encountersQuery$data, _encountersQuery$data2;
    return (_encountersQuery$data = (_encountersQuery$data2 = encountersQuery.data) === null || _encountersQuery$data2 === void 0 ? void 0 : _encountersQuery$data2.pages.flatMap(function (p) {
      return p.encounters;
    })) !== null && _encountersQuery$data !== void 0 ? _encountersQuery$data : [];
  }, [encountersQuery.data]);
  var linkedReports = reports.filter(function (r) {
    return linkedResources.reports.includes(String(r.id));
  });
  var linkedAppointments = allAppointments.filter(function (a) {
    return linkedResources.appointments.includes(a.fhir_id);
  });
  var linkedEncounters = allEncounters.filter(function (e) {
    return linkedResources.encounters.includes(e.fhir_id);
  });
  var hasLinkedRecords = linkedReports.length > 0 || linkedAppointments.length > 0 || linkedEncounters.length > 0;
  return /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-4 pt-3 pb-2.5 px-4 rounded-sds-200 border border-sds-base-default"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex items-center justify-between"
  }, /*#__PURE__*/React.createElement("span", {
    className: "text-style-label-caps text-sds-base-tertiary"
  }, t("label.linked_patient_records", {
    defaultValue: "Linked patient records"
  })), /*#__PURE__*/React.createElement(UnstyledButton, {
    "data-testid": "linked-records-add",
    onClick: function onClick() {
      trackEvent(ANALYTICS_EVENTS.LINKED_RECORDS_ADD, {
        patientId: patientUid
      });
      onAddRecords();
    },
    className: "text-style-label-base text-sds-brand-default underline underline-offset-2 whitespace-nowrap"
  }, t("label.add_records", {
    defaultValue: "Add records"
  }))), /*#__PURE__*/React.createElement("hr", {
    className: "border-t border-sds-base-default"
  }), hasLinkedRecords && /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-3"
  }, linkedReports.map(function (r) {
    return /*#__PURE__*/React.createElement(RecordRow, {
      key: r.id,
      recordId: String(r.id),
      tab: ResourceTab.REPORTS,
      icon: faFileLines,
      name: r.heading,
      date: formatTimestamp(r.updated_at),
      onViewRecords: onViewRecords
    });
  }), linkedAppointments.map(function (a) {
    var _a$appointment_type;
    return /*#__PURE__*/React.createElement(RecordRow, {
      key: a.fhir_id,
      recordId: a.fhir_id,
      tab: ResourceTab.APPOINTMENTS,
      icon: faCalendar,
      name: (_a$appointment_type = a.appointment_type) !== null && _a$appointment_type !== void 0 ? _a$appointment_type : t("label.appointment", {
        defaultValue: "Appointment"
      }),
      date: formatTimestamp(a.start_time),
      onViewRecords: onViewRecords
    });
  }), linkedEncounters.map(function (e) {
    return /*#__PURE__*/React.createElement(RecordRow, {
      key: e.fhir_id,
      recordId: e.fhir_id,
      tab: ResourceTab.ENCOUNTERS,
      icon: faClipboardList,
      name: [e.encounter_class, e.encounter_type].filter(Boolean).join(" | "),
      date: formatTimestamp(e.start_date),
      onViewRecords: onViewRecords
    });
  })));
}